import React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { project, Removable, ResourceTypes } from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useEditResource,
} from "@planzone/view-models";
import { ResourceAvatar } from "@common/resourceAvatar";
import { ContextualMenu } from "@common/contextualMenu";
import { Draggable } from "@common/dragAndDrop";
import { EditIcon } from "@common/editIcon";
import { TrashIcon } from "@common/trashIcon";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { ActivateResourceIcon } from "@root/client/common/activateResourceIcon";
import { DeactivateResourceIcon } from "@root/client/common/deactivatedResourceIcon";
import { UseCases } from "@planzone/core";
import { useModal } from "@root/client/modal";
import { Safeguard } from "@scenes/safeguard";
import { Props } from "./types";
import { RoleSelection } from "./roleSelection";
import { OrganizationalUnitSelection } from "./organizationalUnitSelection";
import { Tag } from "@common/tag";
import { PendingStatus } from "./pendingStatus";
import { Subordinates } from "./subordinates";
import styles from "./styles.scss";

function Resource({ resource, columns, isCompact = false }: Props) {
  const { pushView } = useModal();
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "resource",
  );
  const { isRequestPending, editResource } = useEditResource(resource);
  const { name, profession } = resource;
  const visibleTagsCount = isCompact ? 2 : 4;

  const fixedColMap: {
    [key: string]: (canEditResource: boolean) => React.ReactElement;
  } = {
    tags: () => (
      <div className={styles.tags}>
        {resource.tags.slice(0, visibleTagsCount).map((tag) => (
          <Tag
            key={tag.name}
            project={project}
            tag={tag}
            removable={Removable.RESSOURCE}
            removableId={resource.uuid}
          />
        ))}
        {resource.tags.length > visibleTagsCount && (
          <p className={styles.hiddenTagsNbr}>
            +{resource.tags.length - visibleTagsCount}
          </p>
        )}
      </div>
    ),
    role: (canEditResource) => (
      <div className={styles.roleSelection}>
        {resource.type !== ResourceTypes.MATERIAL && (
          <RoleSelection
            resource={resource}
            readOnly={!canEditResource}
            isCompact={isCompact}
          />
        )}
      </div>
    ),
    invitationStatus: () => (
      <div className={styles.status}>
        {resource.type === ResourceTypes.HUMAN &&
          !resource.invitationAccepted && <PendingStatus />}
      </div>
    ),
    subordinates: () => (
      <div className={styles.subordinatesCol}>
        <Subordinates resource={resource} isCompact={isCompact} />
      </div>
    ),
    organizationalUnit: (canEditResource) => (
      <div className={styles.roleSelection}>
        <OrganizationalUnitSelection
          resource={resource}
          readOnly={!canEditResource}
          isCompact={isCompact}
        />
      </div>
    ),
  };

  return (
    <Safeguard
      useCases={[
        UseCases.retrieveResource,
        UseCases.editResource,
        UseCases.removeResourceFromPlanzone,
        UseCases.addResourceToResourceGroup,
        UseCases.disableResource,
        UseCases.activateResource,
      ]}
    >
      {({
        canRetrieveResource,
        canAddResourceToResourceGroup,
        canEditResource,
        canRemoveResourceFromPlanzone,
        canDisableResource,
        canActivateResource,
      }) => {
        const menu = (canEditResource ||
          canRemoveResourceFromPlanzone ||
          canActivateResource ||
          canDisableResource) && (
          <div className={styles.contextualMenu}>
            <ContextualMenu
              items={[
                ...(canRetrieveResource
                  ? [
                      {
                        name: t("planzoneMembersPage.resources.edit"),
                        renderIcon: (className: string) => (
                          <EditIcon className={className} />
                        ),
                        act: () =>
                          pushView({
                            data: resource.uuid,
                            key: UseCases.retrieveResource,
                          }),
                      },
                    ]
                  : []),
                ...(canDisableResource && !resource.deactivated
                  ? [
                      {
                        name: t("planzoneMembersPage.resources.disable"),
                        renderIcon: (className: string) => (
                          <DeactivateResourceIcon
                            className={classNames(className)}
                          />
                        ),
                        act: () =>
                          pushView({
                            data: resource,
                            key: UseCases.disableResource,
                          }),
                      },
                    ]
                  : []),
                ...(canActivateResource && resource.deactivated
                  ? [
                      {
                        name: t("planzoneMembersPage.resources.activate"),
                        renderIcon: (className: string) => (
                          <ActivateResourceIcon
                            className={classNames(className)}
                          />
                        ),
                        act: () =>
                          pushView({
                            data: resource,
                            key: UseCases.activateResource,
                          }),
                      },
                    ]
                  : []),
                ...(canRemoveResourceFromPlanzone
                  ? [
                      {
                        name: t("removeResourceFromPlanZone.remove"),
                        renderIcon: (className: string) => (
                          <TrashIcon
                            className={classNames(className, styles.icon)}
                          />
                        ),
                        color: "#db5f63",
                        act: () =>
                          pushView({
                            data: resource,
                            key: UseCases.removeResourceFromPlanzone,
                          }),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        );

        return (
          <Draggable
            className={classNames(styles.resource, {
              [styles.readOnly]: !canEditResource,
              [styles.compact]: isCompact,
            })}
            type="MOVE_RESOURCE"
            data={resource}
            isDisabled={!canAddResourceToResourceGroup}
          >
            <React.Fragment>
              {isCompact ? (
                <div className={styles.nameCell}>
                  <div
                    className={styles.user}
                    onClick={() =>
                      canRetrieveResource &&
                      pushView({
                        data: resource.uuid,
                        key: UseCases.retrieveResource,
                      })
                    }
                  >
                    <div className={styles.avatar}>
                      <ResourceAvatar resource={resource} />
                    </div>
                    <div>
                      <p
                        className={
                          resource.deactivated
                            ? `${styles.deactivated}`
                            : styles.name
                        }
                      >
                        {name}
                      </p>
                      {profession && (
                        <p className={styles.title}>{profession}</p>
                      )}
                    </div>
                  </div>
                  {menu}
                </div>
              ) : (
                <div
                  className={styles.user}
                  onClick={() =>
                    canRetrieveResource &&
                    pushView({
                      data: resource.uuid,
                      key: UseCases.retrieveResource,
                    })
                  }
                >
                  <div className={styles.avatar}>
                    <ResourceAvatar resource={resource} />
                  </div>
                  <div>
                    <p
                      className={
                        resource.deactivated
                          ? `${styles.deactivated}`
                          : styles.name
                      }
                    >
                      {name}
                    </p>
                    {profession && <p className={styles.title}>{profession}</p>}
                  </div>
                </div>
              )}
              {columns.map((element) => {
                const criteria = template.find(
                  ({ uuid }) => uuid === element.key,
                );

                return (
                  <div key={element.key} className={styles.col}>
                    {!!criteria ? (
                      <div className={styles.form}>
                        <SecondaryCriteriaForm
                          criteria={criteria}
                          value={
                            resource.attributes
                              ? resource.attributes[criteria.uuid] || ""
                              : ""
                          }
                          flexible
                          disabled={isRequestPending}
                          submit={(value) =>
                            editResource({
                              resource,
                              nextResource: {
                                ...resource,
                                attributes: {
                                  ...resource.attributes,
                                  [criteria.uuid]: value,
                                },
                              },
                            })
                          }
                        />
                      </div>
                    ) : !!fixedColMap[element.key] ? (
                      fixedColMap[element.key](canEditResource)
                    ) : null}
                  </div>
                );
              })}
              {!isCompact && <div>{menu}</div>}
            </React.Fragment>
          </Draggable>
        );
      }}
    </Safeguard>
  );
}

export { Resource };
